import { useState, useCallback, useRef } from 'react';
import { useAlert } from './useAlert';

export const useAudio = (sessionId) => {
	const [isRecording, setIsRecording] = useState(false);
	const [isUploading, setIsUploading] = useState(false);
	const [audioOutputEnabled, setAudioOutputEnabled] = useState(false);
	const [audioInputEnabled, setAudioInputEnabled] = useState(false);
	const [recordedAudioBlob, setRecordedAudioBlob] = useState(null);
	const [currentlyPlayingMessageId, setCurrentlyPlayingMessageId] =
		useState(null);
	const [isAwaitingDecision, setIsAwaitingDecision] = useState(false);

	const { showAlert } = useAlert();
	const baseUrl = process.env.REACT_APP_BASE_URL || '/';

	const mediaRecorderRef = useRef(null);
	const audioChunksRef = useRef([]);
	const streamRef = useRef(null); // Store stream reference
	const audioQueueRef = useRef({
		queue: [],
		isPlaying: false,
		currentAudio: null,
		currentMessageId: null,
		lastPlayedUrl: null,
		synthesis:
			typeof window !== 'undefined' && window.speechSynthesis
				? window.speechSynthesis
				: null,
	});

	// Manual playback session state
	const manualSessionActiveRef = useRef(false);
	const manualAnchorMessageIdRef = useRef(null);

	const toggleAudioInput = useCallback(() => {
		setAudioInputEnabled((prev) => !prev);
	}, []);

	const startRecording = useCallback(async () => {
		if (!audioInputEnabled) {
			console.log('Audio input not enabled');
			return;
		}

		try {
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			streamRef.current = stream;
			const mediaRecorder = new MediaRecorder(stream);

			mediaRecorderRef.current = mediaRecorder;
			audioChunksRef.current = [];

			mediaRecorder.addEventListener('dataavailable', (event) => {
				audioChunksRef.current.push(event.data);
			});

			mediaRecorder.addEventListener('stop', () => {
				setIsRecording(false);

				// Create audio blob and store it for preview instead of auto-uploading
				const audioBlob = new Blob(audioChunksRef.current, {
					type: 'audio/webm',
				});
				setRecordedAudioBlob(audioBlob);

				// Stop all tracks to release the microphone
				if (streamRef.current) {
					streamRef.current.getTracks().forEach((track) => track.stop());
					streamRef.current = null;
				}
			});

			mediaRecorder.start();
			setIsRecording(true);

			// Set a 2-minute timeout
			setTimeout(() => {
				if (
					mediaRecorderRef.current &&
					mediaRecorderRef.current.state !== 'inactive'
				) {
					stopRecording();
					showAlert('Recording reached the 2-minute time limit', 'info');
				}
			}, 2 * 60 * 1000);
		} catch (error) {
			console.error('Error starting recording:', error);
			showAlert(
				'Failed to access microphone. Please check permissions.',
				'danger'
			);
		}
	}, [audioInputEnabled, showAlert]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current && isRecording) {
			mediaRecorderRef.current.stop();
		}
	}, [isRecording]);

	const sendRecording = useCallback(
		async (audioBlob) => {
			if (!audioBlob) return;

			setIsUploading(true);

			try {
				const formData = new FormData();
				formData.append('audio', audioBlob);
				formData.append('participant_id', sessionId);

				const response = await fetch(`${baseUrl}api/messages/upload_audio`, {
					method: 'POST',
					body: formData,
				});

				const data = await response.json();

				if (data.error) {
					showAlert(data.error, 'danger');
				} else {
					// Clear the recorded audio after successful upload
					setRecordedAudioBlob(null);
				}

				setIsUploading(false);
			} catch (error) {
				console.error('Error uploading audio:', error);
				showAlert('Failed to upload audio recording.', 'danger');
				setIsUploading(false);
			}
		},
		[baseUrl, sessionId, showAlert]
	);

	const deleteRecording = useCallback(() => {
		setRecordedAudioBlob(null);
		setIsUploading(false);
	}, []);

	// Clear queue and stop any current playback (define early for dependency usage)
	const clearAudioQueue = useCallback(() => {
		const queue = audioQueueRef.current;

		queue.queue = [];

		if (queue.currentAudio) {
			queue.currentAudio.pause();
			URL.revokeObjectURL(queue.currentAudio.src);
			queue.currentAudio = null;
		}

		if (queue.synthesis && queue.synthesis.speaking) {
			queue.synthesis.cancel();
		}

		queue.isPlaying = false;
		queue.currentMessageId = null;
		setCurrentlyPlayingMessageId(null);
		// Reset manual session state and decision state
		manualSessionActiveRef.current = false;
		manualAnchorMessageIdRef.current = null;
		setIsAwaitingDecision(false);
	}, []);

	const playAudio = useCallback(
		(audioURL, messageId, isAutoPlay = false) => {
			if (!audioOutputEnabled || !audioURL) return false;

			const queue = audioQueueRef.current;

			// Manual play: clear existing queue and set manual session anchor
			if (!isAutoPlay) {
				// Stop anything currently playing and clear pending items
				// Also resets decision state
				clearAudioQueue();
				manualSessionActiveRef.current = true;
				manualAnchorMessageIdRef.current = messageId;
				setIsAwaitingDecision(false);
			}

			// Add participant_id to the URL
			const fullUrl = `${audioURL}&participant_id=${sessionId}`;

			// Add to queue
			queue.queue.push({ url: fullUrl, messageId });

			// Start playing if not already playing
			if (!queue.isPlaying) {
				playNextInQueue();
			}

			return true;
		},
		[audioOutputEnabled, sessionId, clearAudioQueue]
	);

	const playNextInQueue = useCallback(async () => {
		const queue = audioQueueRef.current;

		if (queue.isPlaying || queue.queue.length === 0) {
			return;
		}

		queue.isPlaying = true;
		const { url, messageId } = queue.queue.shift();
		queue.lastPlayedUrl = url;
		queue.currentMessageId = messageId;

		// Update state to trigger re-render
		setCurrentlyPlayingMessageId(messageId);

		try {
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const blob = await response.blob();

			if (!blob.type.startsWith('audio/')) {
				throw new Error('Invalid audio format');
			}

			const audioUrl = URL.createObjectURL(blob);

			if (queue.currentAudio) {
				queue.currentAudio.pause();
				URL.revokeObjectURL(queue.currentAudio.src);
			}

			queue.currentAudio = new Audio();
			queue.currentAudio.src = audioUrl;

			queue.currentAudio.onended = () => {
				URL.revokeObjectURL(audioUrl);
				queue.isPlaying = false;
				queue.currentMessageId = null;
				setCurrentlyPlayingMessageId(null);
				// If this was a manual session and we're at the end, pause for decision
				if (
					manualSessionActiveRef.current &&
					(messageId === manualAnchorMessageIdRef.current ||
						queue.queue.length === 0)
				) {
					queue.currentAudio = null;
					setIsAwaitingDecision(true);
					return;
				}
				playNextInQueue();
			};

			queue.currentAudio.onerror = (e) => {
				console.error('Audio playback error:', e);
				URL.revokeObjectURL(audioUrl);
				queue.isPlaying = false;
				queue.currentMessageId = null;
				setCurrentlyPlayingMessageId(null);

				// Try speech synthesis fallback
				tryWebSpeechFallback(messageId);

				// If this was a manual session and we're at the end, pause for decision
				if (
					manualSessionActiveRef.current &&
					(messageId === manualAnchorMessageIdRef.current ||
						queue.queue.length === 0)
				) {
					queue.currentAudio = null;
					setIsAwaitingDecision(true);
					return;
				}

				playNextInQueue();
			};

			await queue.currentAudio.play();
		} catch (error) {
			console.error('Audio playback error:', error);
			queue.isPlaying = false;
			queue.currentMessageId = null;
			setCurrentlyPlayingMessageId(null);

			// Try speech synthesis fallback
			tryWebSpeechFallback(messageId);

			// If this was a manual session and we're at the end, pause for decision
			if (
				manualSessionActiveRef.current &&
				(messageId === manualAnchorMessageIdRef.current ||
					queue.queue.length === 0)
			) {
				const queue = audioQueueRef.current;
				queue.currentAudio = null;
				setIsAwaitingDecision(true);
				return;
			}

			playNextInQueue();
		}
	}, []);

	const tryWebSpeechFallback = useCallback((messageId) => {
		const queue = audioQueueRef.current;

		if (!queue.synthesis) {
			return;
		}

		// Find message text by messageId in DOM
		const messageElement = document.querySelector(
			`[data-message-id="${messageId}"]`
		);
		if (!messageElement) {
			return;
		}

		let messageText = messageElement.textContent;
		if (messageText.includes(':')) {
			messageText = messageText.split(':')[1].trim();
		}

		const utterance = new SpeechSynthesisUtterance(messageText);

		utterance.onend = () => {
			queue.isPlaying = false;
			queue.currentMessageId = null;
			setCurrentlyPlayingMessageId(null);
			// If this was a manual session and we're at the end, pause for decision
			if (
				manualSessionActiveRef.current &&
				(messageId === manualAnchorMessageIdRef.current ||
					audioQueueRef.current.queue.length === 0)
			) {
				setIsAwaitingDecision(true);
				return;
			}
			playNextInQueue();
		};

		utterance.onerror = () => {
			queue.isPlaying = false;
			queue.currentMessageId = null;
			setCurrentlyPlayingMessageId(null);
			// If this was a manual session and we're at the end, pause for decision
			if (
				manualSessionActiveRef.current &&
				(messageId === manualAnchorMessageIdRef.current ||
					audioQueueRef.current.queue.length === 0)
			) {
				setIsAwaitingDecision(true);
				return;
			}
			playNextInQueue();
		};

		queue.synthesis.speak(utterance);
	}, []);

	const pauseAudio = useCallback(() => {
		const queue = audioQueueRef.current;

		if (queue.currentAudio && queue.isPlaying) {
			queue.currentAudio.pause();
			queue.isPlaying = false;
			queue.currentMessageId = null;
			setCurrentlyPlayingMessageId(null);
		}

		if (queue.synthesis && queue.synthesis.speaking) {
			queue.synthesis.pause();
		}
	}, []);

	const resumeAudio = useCallback(() => {
		const queue = audioQueueRef.current;

		if (queue.currentAudio && !queue.isPlaying) {
			queue.currentAudio.play();
			queue.isPlaying = true;
		} else if (!queue.currentAudio && queue.queue.length > 0) {
			playNextInQueue();
		}

		if (queue.synthesis && queue.synthesis.paused) {
			queue.synthesis.resume();
		}
	}, [playNextInQueue]);

	// Replace queue and start playing (used by decision actions)
	const replaceQueueAndStart = useCallback(
		(items) => {
			// items: Array<{ url: string, messageId: any }>, url WITHOUT participant_id
			clearAudioQueue();
			const queue = audioQueueRef.current;
			const prepared = items.map(({ url, messageId }) => ({
				url: `${url}&participant_id=${sessionId}`,
				messageId,
			}));
			queue.queue.push(...prepared);
			setIsAwaitingDecision(false);
			if (!queue.isPlaying) {
				playNextInQueue();
			}
		},
		[sessionId, clearAudioQueue, playNextInQueue]
	);

	const continueFrom = useCallback(
		(nextItem) => {
			// nextItem: { url: string, messageId: any }
			clearAudioQueue();
			const queue = audioQueueRef.current;
			const prepared = [
				{
					url: `${nextItem.url}&participant_id=${sessionId}`,
					messageId: nextItem.messageId,
				},
			];
			queue.queue.push(...prepared);
			// Keep manual session active and advance the anchor to the next item
			manualSessionActiveRef.current = true;
			manualAnchorMessageIdRef.current = nextItem.messageId;
			setIsAwaitingDecision(false);
			if (!queue.isPlaying) {
				playNextInQueue();
			}
		},
		[sessionId, clearAudioQueue, playNextInQueue]
	);

	const skipToLatest = useCallback(
		(latestItem) => {
			if (!latestItem) {
				// Nothing to play
				setIsAwaitingDecision(false);
				manualSessionActiveRef.current = false;
				manualAnchorMessageIdRef.current = null;
				return;
			}
			// Reset manual session and play only the latest
			manualSessionActiveRef.current = false;
			manualAnchorMessageIdRef.current = null;
			replaceQueueAndStart([latestItem]);
		},
		[replaceQueueAndStart]
	);

	const toggleAudioOutput = useCallback(() => {
		setAudioOutputEnabled((prev) => {
			const newValue = !prev;
			// If turning off audio output, stop any currently playing audio
			if (!newValue) {
				clearAudioQueue();
			}
			return newValue;
		});
	}, [clearAudioQueue]);

	return {
		isRecording,
		isUploading,
		audioOutputEnabled,
		audioInputEnabled,
		recordedAudioBlob,
		currentlyPlayingMessageId,
		isAwaitingDecision,
		manualSessionActive: manualSessionActiveRef.current,
		manualAnchorMessageId: manualAnchorMessageIdRef.current,
		toggleAudioOutput,
		toggleAudioInput,
		startRecording,
		stopRecording,
		sendRecording,
		deleteRecording,
		playAudio,
		pauseAudio,
		resumeAudio,
		clearAudioQueue,
		continueFrom,
		skipToLatest,
	};
};
